import React, { useState, useRef } from 'react';
import { ArrowDown } from 'lucide-react';
import { isAfter } from 'date-fns';
import { coerceToUtcDate } from '@/lib/date-utils';
import { DatePickerButton } from './DatePickerButton';

const RANGE_ERROR = 'End date must be after start date';

type Side = 'from' | 'to';

export interface DateRangeValuesSectionProps {
  selectedValues: string[];
  onRangeChange: (startDate: string, endDate: string) => void;
  validationErrors?: string[];
}

/**
 * DateRangeValuesSection - Date range picker for date-based filters
 * Used for DATE column types with BETWEEN operator
 * Values are stored as two UTC ISO instants, edited in the user's display
 * time zone. selectedValues[0] = start, selectedValues[1] = end
 */
export const DateRangeValuesSection: React.FC<DateRangeValuesSectionProps> = ({
  selectedValues,
  onRangeChange,
  validationErrors = [],
}) => {
  const [fromError, toError] = validationErrors;
  const [fromValue = '', toValue = ''] = selectedValues;
  const fromDate = coerceToUtcDate(fromValue) ?? undefined;
  const toDate = coerceToUtcDate(toValue) ?? undefined;

  // Each picker popover is parent-controlled so Apply can close it and hand
  // focus to the sibling trigger (or keep it open on an inverted range).
  const [openSide, setOpenSide] = useState<Side>();
  // Which picker's Apply was rejected; the error shows inside its popover.
  const [rangeErrorOn, setRangeErrorOn] = useState<Side>();
  const fromTriggerRef = useRef<HTMLButtonElement>(null);
  const toTriggerRef = useRef<HTMLButtonElement>(null);

  // A picker's Apply stages its half into the draft, passing the other half
  // through as stored ('' when missing, so Apply validates what the user can
  // see); the filter still commits only via the filter's own Apply. A range
  // whose To is not after its From is not staged and keeps the popover open
  // with the error. Compared as the exact instants the backend applies: an
  // untouched half can carry seconds the pickers don't show, so To 14:30:00
  // before From 14:30:37 would match nothing, and equal instants match only
  // rows at that instant. A date-only To (an older link or saved filter)
  // spans its whole UTC day.
  const handleApply = (side: Side, value: string): void => {
    const [from, to] = side === 'from' ? [value, toValue] : [fromValue, value];
    const start = coerceToUtcDate(from);
    const end = coerceToUtcDate(to); // a fresh Date, safe to move
    if (end && /^\d{4}-\d{2}-\d{2}$/.test(to)) end.setUTCHours(23, 59, 59, 999);
    if (start && end && !isAfter(end, start)) {
      setRangeErrorOn(side);
      return;
    }
    setRangeErrorOn(undefined);
    onRangeChange(from, to);
    setOpenSide(undefined);
  };

  // Closing a picker without an accepted Apply (Cancel, Escape, outside
  // click) drops its rejected draft, and so its error. Editing the draft
  // drops the error too, since the edit may have fixed it.
  const handleOpenChange = (side: Side, open: boolean): void => {
    setOpenSide(open ? side : undefined);
    setRangeErrorOn(undefined);
  };
  const clearRangeError = (): void => setRangeErrorOn(undefined);

  return (
    <div className="flex flex-col gap-3 p-4">
      {/* From Date Picker */}
      <div className="flex flex-col gap-2">
        <label className="text-xs font-medium text-muted-foreground">
          From
        </label>
        <DatePickerButton
          value={fromValue}
          onApply={value => handleApply('from', value)}
          onDraftChange={clearRangeError}
          placeholder="Select start date"
          rangeStart={fromDate}
          rangeEnd={toDate}
          open={openSide === 'from'}
          onOpenChange={open => handleOpenChange('from', open)}
          triggerRef={fromTriggerRef}
          focusOnCloseRef={toTriggerRef}
          error={rangeErrorOn === 'from' ? RANGE_ERROR : undefined}
        />
        {fromError && (
          <div className="text-xs text-destructive">{fromError}</div>
        )}
      </div>

      {/* Arrow Indicator */}
      <div className="flex items-center justify-center">
        <ArrowDown className="h-4 w-4 text-muted-foreground" />
      </div>

      {/* To Date Picker */}
      <div className="flex flex-col gap-2">
        <label className="text-xs font-medium text-muted-foreground">To</label>
        <DatePickerButton
          value={toValue}
          onApply={value => handleApply('to', value)}
          onDraftChange={clearRangeError}
          placeholder="Select end date"
          rangeStart={fromDate}
          rangeEnd={toDate}
          open={openSide === 'to'}
          onOpenChange={open => handleOpenChange('to', open)}
          triggerRef={toTriggerRef}
          focusOnCloseRef={fromTriggerRef}
          error={rangeErrorOn === 'to' ? RANGE_ERROR : undefined}
        />
        {toError && <div className="text-xs text-destructive">{toError}</div>}
      </div>
    </div>
  );
};
